import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { TimesheetReport } from "domain/entities/timesheet-report";
import React from "react";
import logo from "../../../assets/logo.png";
import { useAuthStore } from "presentation/stores/use-auth-store";

const MAX_ROWS_PER_PAGE = 24;

type ReportRow = {
  day: number;
  description: string;
  abscence: number | string;
  allocation: number | string;
  support: number | string;
};

type SectionTotals = {
  abscence: number;
  allocation: number;
  support: number;
};

type ReportSection = {
  key: string;
  title: string;
  rows: ReportRow[];
  totals: SectionTotals;
};

type ReportPage = {
  section: ReportSection;
  rows: ReportRow[];
};



const styles = StyleSheet.create({
  page: { padding: 60, fontSize: 10, fontFamily: "Helvetica" },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },

  logo: { width: 130, height: 15 },

  title: {
    fontSize: 14,
    textAlign: "center",
    marginVertical: 10,
    fontWeight: "bold",
  },

  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },

  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#000",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ccc",
  },

  tableCell: {
    flex: 1,
    padding: 4,
    borderRightWidth: 1,
    borderColor: "#ccc",
    display: "flex",
    alignItems: "center",
  },

  tableCellNoBorder: {
    flex: 1,
    padding: 4,
  },

  descriptionCell: {
    flex: 2.5,
    padding: 4,
    borderRightWidth: 1,
    borderColor: "#ccc",
    justifyContent: "center",
    display: "flex",
  },

  lastCell: {
    borderRightWidth: 0,
  },

  bold: {
    fontWeight: "bold",
  },

  centerCell: {
    textAlign: "center",
  },

  logoUnderline: {
    borderBottomWidth: 1,
    borderColor: "#000",
    width: "100%",
  },

  backgroundGray: {
    backgroundColor: "#f0f0f0",
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 5,
  },

  footer: {
    position: "absolute",
    bottom: 30,
    left: 0,
    right: 60,
    textAlign: "right",
    fontSize: 10,
  },

  // ─── Summary Page Styles ───
  summaryTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginVertical: 15,
    color: "#2C3E50",
  },

  summarySubtitle: {
    fontSize: 11,
    textAlign: "center",
    marginBottom: 10,
    color: "#7F8C8D",
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 5,
    backgroundColor: "#E0E0E0",
    color: "#000000",
    padding: 6,
    borderRadius: 3,
  },

  summaryTable: {
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#BDC3C7",
    borderRadius: 3,
  },

  summaryTableHeader: {
    flexDirection: "row",
    backgroundColor: "#ECF0F1",
    borderBottomWidth: 1,
    borderColor: "#BDC3C7",
  },

  summaryHeaderCell: {
    flex: 2,
    padding: 5,
    fontWeight: "bold",
    color: "#2C3E50",
    fontSize: 10,
  },

  summaryHeaderCellRight: {
    flex: 1,
    padding: 5,
    fontWeight: "bold",
    color: "#2C3E50",
    fontSize: 10,
    textAlign: "center",
  },

  summaryRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ECF0F1",
  },

  summaryCell: {
    flex: 2,
    padding: 4,
    fontSize: 9,
    color: "#34495E",
  },

  summaryCellRight: {
    flex: 1,
    padding: 4,
    fontSize: 9,
    color: "#34495E",
    textAlign: "center",
  },

  summaryTotalRow: {
    flexDirection: "row",
    backgroundColor: "#D5DBDB",
    fontWeight: "bold",
  },

  summaryTotalCell: {
    flex: 2,
    padding: 4,
    fontWeight: "bold",
    fontSize: 10,
    color: "#2C3E50",
  },

  summaryTotalCellRight: {
    flex: 1,
    padding: 4,
    fontWeight: "bold",
    fontSize: 10,
    color: "#2C3E50",
    textAlign: "center",
  },

  grandTotalRow: {
    flexDirection: "row",
    backgroundColor: "#E0E0E0",
    marginTop: 10,
    padding: 8,
    borderRadius: 3,
  },

  grandTotalLabel: {
    flex: 2,
    color: "#000000",
    fontWeight: "bold",
    fontSize: 14,
  },

  grandTotalValue: {
    flex: 1,
    color: "#000000",
    fontWeight: "bold",
    fontSize: 14,
    textAlign: "center",
  },

  emptyText: {
    fontSize: 9,
    color: "#888",
    fontStyle: "italic",
    padding: 4,
    textAlign: "center",
  },
});

type Props = {
  data: TimesheetReport;
  isSuportService: boolean;
};

const TimesheetReportPDF: React.FC<Props> = ({ data, isSuportService }) => {
  const { userEmployee } = useAuthStore();

  const sections = new Map<string, ReportSection>();

  data.days.forEach((day) => {
    day.activities.forEach((activity) => {
      const isAbsence = activity.type === "Abscence";
      const isSupportOrDeslocation =
        activity.name.toLowerCase().includes("prevenção") ||
        activity.name.toLowerCase().includes("deslocação");
      const key = isAbsence
        ? "absences"
        : `po:${activity.purchaseOrderId ?? activity.name}`;
      const section = sections.get(key) ?? {
        key,
        title: isAbsence ? "Ausências" : activity.name,
        rows: [],
        totals: { abscence: 0, allocation: 0, support: 0 },
      };
      const row: ReportRow = {
        day: day.day,
        description: activity.name,
        abscence: isAbsence ? activity.hours : "",
        allocation: !isAbsence && !isSupportOrDeslocation ? activity.hours : "",
        support: !isAbsence && isSupportOrDeslocation ? 1 : "",
      };

      section.rows.push(row);
      section.totals.abscence += isAbsence ? activity.hours : 0;
      section.totals.allocation +=
        !isAbsence && !isSupportOrDeslocation ? activity.hours : 0;
      section.totals.support += !isAbsence && isSupportOrDeslocation ? 1 : 0;
      sections.set(key, section);
    });
  });

  const reportSections = Array.from(sections.values());
  const pages: ReportPage[] = reportSections.flatMap((section) => {
    const sectionPages: ReportPage[] = [];
    for (let i = 0; i < section.rows.length; i += MAX_ROWS_PER_PAGE) {
      sectionPages.push({
        section,
        rows: section.rows.slice(i, i + MAX_ROWS_PER_PAGE),
      });
    }
    return sectionPages;
  });

  const workHoursPerDay = userEmployee?.workHoursPerDay ?? 8;

  // ──────────────────────────────────────────────
  // Compute summaries for the totals page
  // ──────────────────────────────────────────────
  const poSummaryMap = new Map<string, { hours: number; support: number }>();
  const absenceSummaryMap = new Map<string, number>();

  data.days.forEach((dayRecord) => {
    dayRecord.activities.forEach((activity) => {
      const isAbsence = activity.type === "Abscence";
      const isSupportOrDeslocation =
        activity.name.toLowerCase().includes("prevenção") ||
        activity.name.toLowerCase().includes("deslocação");

      if (isAbsence) {
        const current = absenceSummaryMap.get(activity.name) ?? 0;
        absenceSummaryMap.set(activity.name, current + activity.hours);
      } else {
        const current = poSummaryMap.get(activity.name) ?? {
          hours: 0,
          support: 0,
        };
        if (isSupportOrDeslocation) {
          current.support += 1;
        } else {
          current.hours += activity.hours;
        }
        poSummaryMap.set(activity.name, current);
      }
    });
  });

  const poSummaries = Array.from(poSummaryMap.entries())
    .map(([name, totals]) => ({
      name,
      totalHours: totals.hours,
      totalSupport: totals.support,
    }))
    .sort(
      (a, b) =>
        b.totalHours + b.totalSupport - (a.totalHours + a.totalSupport),
    );

  const absenceSummaries = Array.from(absenceSummaryMap.entries())
    .map(([name, totalHours]) => ({ name, totalHours }))
    .sort((a, b) => b.totalHours - a.totalHours);

  const totalPO = poSummaries.reduce((sum, item) => sum + item.totalHours, 0);
  const totalSupport = poSummaries.reduce(
    (sum, item) => sum + item.totalSupport,
    0,
  );
  const totalAbsences = absenceSummaries.reduce(
    (sum, item) => sum + item.totalHours,
    0,
  );
  const grandTotal = totalPO + totalAbsences;

  return (
    <Document>
      {pages.map((page, pageIndex) => (
        <Page style={styles.page} size="A4" key={pageIndex}>
          {/* Header */}
          <View style={styles.headerRow}>
            <Image src={logo} style={styles.logo} />
          </View>
          <View style={styles.logoUnderline} />

          <Text style={styles.title}>
            Mapa de Horas – {String(data.month).padStart(2, "0")}/{data.year} -{" "}
            {page.section.title}
          </Text>

          {/* Info */}
          <View style={styles.infoRow}>
            <View>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Colaborador: </Text>
                {data.name}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>E-mail: </Text>
                {data.email}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Manager: </Text>
                {data.managerName}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>E-mail manager: </Text>
                {data.managerEmail ?? "SEM MANAGER"}
              </Text>
            </View>
            <View>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Versão: </Text>
                {data.version}
              </Text>
              <Text>
                <Text style={{ fontWeight: "bold" }}>Status: </Text>
                {data.status}
              </Text>
            </View>
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, styles.bold, styles.centerCell]}>
                Dia
              </Text>
              <Text style={[styles.descriptionCell, styles.bold]}>
                Descrição
              </Text>
              <Text style={[styles.tableCell, styles.bold, styles.centerCell]}>
                Ausências
              </Text>
              <Text style={[styles.tableCell, styles.bold, styles.centerCell]}>
                Alocações
              </Text>
              {isSuportService && (
                <Text
                  style={[
                    styles.tableCell,
                    styles.bold,
                    styles.lastCell,
                    styles.centerCell,
                  ]}
                >
                  Prevenção ou Deslocação
                </Text>
              )}
            </View>

            {/* Table Rows */}
            {page.rows.map((row, index) => (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {row.day}
                </Text>
                <Text style={styles.descriptionCell}>{row.description}</Text>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {row.abscence}
                </Text>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {row.allocation}
                </Text>
                {isSuportService && (
                  <Text
                    style={[
                      styles.tableCell,
                      styles.lastCell,
                      styles.centerCell,
                    ]}
                  >
                    {row.support}
                  </Text>
                )}
              </View>
            ))}

            {/* Totals for the section are repeated so each page is self-contained. */}
            <>
              <View style={styles.tableRow}>
                <Text
                  style={[styles.tableCellNoBorder, styles.backgroundGray]}
                />
                <Text style={[styles.descriptionCell, styles.backgroundGray]} />
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {page.section.totals.abscence}
                </Text>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {page.section.totals.allocation}
                </Text>
                {isSuportService && (
                  <Text
                    style={[styles.tableCellNoBorder, styles.backgroundGray]}
                  />
                )}
              </View>

              <View style={styles.tableRow}>
                <Text
                  style={[styles.tableCellNoBorder, styles.backgroundGray]}
                />
                <Text style={[styles.descriptionCell, styles.backgroundGray]} />
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {(page.section.totals.abscence / workHoursPerDay).toFixed(2)}{" "}
                  dias
                </Text>
                <Text style={[styles.tableCell, styles.centerCell]}>
                  {(page.section.totals.allocation / workHoursPerDay).toFixed(
                    2,
                  )}{" "}
                  dias
                </Text>
                {isSuportService && (
                  <Text
                    style={[
                      styles.tableCell,
                      styles.lastCell,
                      styles.centerCell,
                    ]}
                  >
                    {page.section.totals.support} dias
                  </Text>
                )}
              </View>
            </>
          </View>

          <Text style={styles.footer}>
            Página {pageIndex + 1}/{pages.length} - {page.section.title}
          </Text>
        </Page>
      ))}

      {/* ═══════════════════════════════════════════════════════
          SUMMARY PAGE — Totals by PO and Absence Type
         ═══════════════════════════════════════════════════════ */}
      <Page style={styles.page} size="A4">
        {/* Header */}
        <View style={styles.headerRow}>
          <Image src={logo} style={styles.logo} />
        </View>
        <View style={styles.logoUnderline} />

        <Text style={styles.summaryTitle}>
          Resumo do Mês — Totais por PO e Tipo de Ausência
        </Text>
        <Text style={styles.summarySubtitle}>
          {data.name} — {String(data.month).padStart(2, "0")}/{data.year}
        </Text>

        {/* ─── Section: Totals by PO ─── */}
        <Text style={styles.sectionTitle}>Horas por PO (Ordem de Compra)</Text>

        {poSummaries.length === 0 ? (
          <Text style={styles.emptyText}>Nenhuma hora registada em POs.</Text>
        ) : (
          <View style={styles.summaryTable}>
            <View style={styles.summaryTableHeader}>
              <Text style={styles.summaryHeaderCell}>PO (Ordem de Compra)</Text>
              <Text style={styles.summaryHeaderCellRight}>Total Horas</Text>
              <Text style={styles.summaryHeaderCellRight}>Prev/Desl (Dias)</Text>
            </View>
            {poSummaries.map((item, idx) => (
              <View
                style={[
                  styles.summaryRow,
                  idx % 2 === 1 ? { backgroundColor: "#F8F9F9" } : {},
                ]}
                key={idx}
              >
                <Text style={styles.summaryCell}>{item.name}</Text>
                <Text style={styles.summaryCellRight}>
                  {item.totalHours.toFixed(1)}h
                </Text>
                <Text style={styles.summaryCellRight}>{item.totalSupport}</Text>
              </View>
            ))}
            <View style={styles.summaryTotalRow}>
              <Text style={styles.summaryTotalCell}>Total PO</Text>
              <Text style={styles.summaryTotalCellRight}>
                {totalPO.toFixed(1)}h
              </Text>
              <Text style={styles.summaryTotalCellRight}>{totalSupport}</Text>
            </View>
          </View>
        )}

        {/* ─── Section: Totals by Absence Type ─── */}
        <Text style={styles.sectionTitle}>Horas por Tipo de Ausência</Text>

        {absenceSummaries.length === 0 ? (
          <Text style={styles.emptyText}>Nenhuma ausência registada.</Text>
        ) : (
          <View style={styles.summaryTable}>
            <View style={styles.summaryTableHeader}>
              <Text style={styles.summaryHeaderCell}>Tipo de Ausência</Text>
              <Text style={styles.summaryHeaderCellRight}>Total Horas</Text>
              <Text style={styles.summaryHeaderCellRight}></Text>
            </View>
            {absenceSummaries.map((item, idx) => (
              <View
                style={[
                  styles.summaryRow,
                  idx % 2 === 1 ? { backgroundColor: "#F8F9F9" } : {},
                ]}
                key={idx}
              >
                <Text style={styles.summaryCell}>{item.name}</Text>
                <Text style={styles.summaryCellRight}>
                  {item.totalHours.toFixed(1)}h
                </Text>
                <Text style={styles.summaryCellRight}></Text>
              </View>
            ))}
            <View style={styles.summaryTotalRow}>
              <Text style={styles.summaryTotalCell}>Total Ausências</Text>
              <Text style={styles.summaryTotalCellRight}>
                {totalAbsences.toFixed(1)}h
              </Text>
              <Text style={styles.summaryTotalCellRight}></Text>
            </View>
          </View>
        )}

        {/* ─── Grand Total ─── */}
        <View style={styles.grandTotalRow}>
          <Text style={styles.grandTotalLabel}>TOTAL GERAL DO MÊS</Text>
          <Text style={styles.grandTotalValue}>{grandTotal.toFixed(1)}h</Text>
          <Text style={styles.grandTotalValue}></Text>
        </View>

        <Text style={styles.footer}>
          Página {pages.length + 1} de {pages.length + 1}
        </Text>
      </Page>
    </Document>
  );
};

export default TimesheetReportPDF;
